import { graphql, PageProps, Slice } from 'gatsby';
import React from 'react';

import { Breadcrumbs } from '../../../../components/breadcrumbs';
import { EditorialPage } from '../../../../components/page-components/editorial-page/editorial-page';
import { Seo } from '../../../../components/seo/seo';
import { extractTenantData } from '../../../../helpers/editorial-page/editorial-page-helpers';
import { getSeoDescription } from '../../../../helpers/seo-description';
import useGetMobileAppWebViewHeader from '../../../../hooks/use-get-mobile-app-web-view';
import { IMeetingPlacePageEditorial } from '../../../../queries/fragments/meetingPlacePageEditorial';
import { IMeetingPlacePageHomeMinifiedExtendedTenantOpeningHours } from '../../../../queries/fragments/meetingPlacePageHome-minified';
import { PageContext } from '../../../page-context.interface';

interface MeetingPlacePageEditorialProps {
  contentfulMeetingPlacePageEditorial: IMeetingPlacePageEditorial;
  contentfulMeetingPlacePageHome: IMeetingPlacePageHomeMinifiedExtendedTenantOpeningHours;
}

const MeetingPlacePageEditorial = ({
  data,
  pageContext,
}: PageProps<MeetingPlacePageEditorialProps, PageContext>) => {
  const page = data?.contentfulMeetingPlacePageEditorial;
  const homepage = data?.contentfulMeetingPlacePageHome;
  const meetingPlaceOpeningHours = homepage?.meetingPlace?.openingHours;
  const meetingPlaceOpeningHoursExceptions =
    homepage?.meetingPlace?.openingHoursExceptions;
  const isWebView = useGetMobileAppWebViewHeader();

  const filteredTenants = extractTenantData(homepage, pageContext);

  return (
    <>
      <Slice alias="layout" pageContext={{ ...pageContext }}>
        <main className="bg-background-primary-light px-6 lg:-ml-22 lg:bg-transparent lg:px-0">
          <Breadcrumbs
            breadcrumbs={[page?.name]}
            homeText={homepage?.breadcrumbHomeText}
            cssClass={`lg:ml-22 ${isWebView ? '!pt-8' : ''}`}
            node_locale={pageContext?.language}
          />
          <EditorialPage
            page={page}
            integrationId={homepage?.meetingPlace?.integrationId}
            node_locale={pageContext?.language}
            showSocialShare={homepage?.meetingPlace?.showSocialShare}
            meetingPlaceOpeningHours={{
              openingHours: meetingPlaceOpeningHours,
              openingHoursExceptions: meetingPlaceOpeningHoursExceptions,
              timezone: homepage?.meetingPlace?.timezone ?? '',
            }}
            tenantOpeningHoursDetails={filteredTenants}
          />
        </main>
      </Slice>
      <Slice alias="footer" footerClass={`${isWebView ? 'hidden' : 'block'}`} />
    </>
  );
};
export default MeetingPlacePageEditorial;

export const query = graphql`
  query MeetingPlacePageEditorialQuery(
    $id: String!
    $language: String!
    $tagId: String!
    $languages: [String!]
    $openingHours: Boolean!
  ) {
    contentfulMeetingPlacePageEditorial(
      id: { eq: $id }
      node_locale: { in: $languages }
    ) {
      ...MeetingPlacePageEditorial
      editorialPageCards {
        ...MeetingPlacePageEditorial
      }
    }
    contentfulMeetingPlacePageHome(
      metadata: { tags: { elemMatch: { contentful_id: { eq: $tagId } } } }
      node_locale: { eq: $language }
    ) {
      ...MeetingPlacePageHomeMinified
      ...MeetingPlacePageHomeMinifiedExtendedTenantOpeningHours
        @include(if: $openingHours)
    }
  }
`;

export const Head = ({
  data,
  pageContext,
}: PageProps<MeetingPlacePageEditorialProps, PageContext>) => {
  const page = data?.contentfulMeetingPlacePageEditorial;
  const homepage = data?.contentfulMeetingPlacePageHome;

  const seoDescription = getSeoDescription(
    page.seoDescription?.seoDescription ?? '',
    page.body?.raw ?? '',
  );
  const image = page?.image ? page.image : undefined;

  const seoImageUrl =
    image && image.file && image.file.url
      ? `https:${image?.file?.url}`
      : undefined;

  return (
    <Seo
      title={page?.name}
      homepage={homepage}
      page={page}
      description={seoDescription}
      imageUrl={seoImageUrl}
      pageContext={pageContext}
    />
  );
};
